import { format, parse } from 'date-fns';
import {
  ACIS,
  BIN,
  BREP,
  Blender,
  CATIA,
  COLLADA,
  CREO,
  DWG,
  DXF,
  Dynamo,
  Excel,
  FBX,
  GLTF,
  Grasshopper,
  IFC,
  IGES,
  JSON,
  JT,
  NX,
  OBJ,
  PDF,
  PLY,
  PRC,
  RHINO,
  Revit,
  SOLIDEDGE,
  SOLIDWORKS,
  STEP,
  STL,
  ThreeDS,
  ThreeDXML,
  ThreeMF,
  U3D,
  USD,
  Unity,
  VRML,
  X3D,
  X_T,
} from './images';
import { logDebug } from '../config/globalFunctions';

/** @typedef {import('../../../global').CrudType} CrudType */

/**
 * CRUD constants
 * @type {{CREATE: 0, READ: 1, UPDATE: 2, DELETE: 3}}
 */
export const CRUD = {
  CREATE: 0,
  READ: 1,
  UPDATE: 2,
  DELETE: 3,
};

/**
 *
 * @returns
 */
export const getFileTypeIcon = (extension) => {
  switch (extension) {
    case '.pdf':
      return PDF;
    case '.3ds':
      return ThreeDS;
    case '.3dxml':
      return ThreeDXML;
    case '.3mf':
      return ThreeMF;
    case '.sat':
      return ACIS;
    case '.sab':
      return ACIS;
    case '.u3d':
      return U3D;
    case '.dwg':
      return DWG;
    case '.blend':
      return Blender;
    case '.brep':
      return BREP;
    case '.cat':
      return CATIA;
    case '.dae':
      return COLLADA;
    case '.prt.':
      return CREO;
    case '.asm.':
      return CREO;
    case '.dxf':
      return DXF;
    case '.dyn':
      return Dynamo;
    case '.xls':
      return Excel;
    case '.xlsx':
      return Excel;
    case '.fbx':
      return FBX;
    case '.glb':
      return GLTF;
    case '.gltf':
      return GLTF;
    case '.gh':
      return Grasshopper;
    case '.ghx':
      return Grasshopper;
    case '.ifc':
      return IFC;
    case '.igs':
      return IGES;
    case '.iges':
      return IGES;
    case '.jt':
      return JT;
    case '.prt':
      return NX;
    case '.obj':
      return OBJ;
    case '.x_t':
      return X_T;
    case '.x_b':
      return X_T;
    case '.xmt_txt':
      return X_T;
    case '.xmt_bin':
      return X_T;
    case '.xmb_txt':
      return X_T;
    case '.xmb_bin':
      return X_T;
    case '.ply':
      return PLY;
    case '.prc':
      return PRC;
    case '.rvt':
      return Revit;
    case '.rfa':
      return Revit;
    case '.rte':
      return Revit;
    case '.rft':
      return Revit;
    case '.3dm':
      return RHINO;
    case '.par':
      return SOLIDEDGE;
    case '.psm':
      return SOLIDEDGE;
    case '.asm':
      return SOLIDEDGE;
    case '.sldprt':
      return SOLIDWORKS;
    case '.sldasm':
      return SOLIDWORKS;
    case '.stp':
      return STEP;
    case '.step':
      return STEP;
    case '.stl':
      return STL;
    case '.unity':
      return Unity;
    case '.usd':
      return USD;
    case '.usda':
      return USD;
    case '.usdc':
      return USD;
    case '.usdz':
      return USD;
    case '.wrl':
      return VRML;
    case '.x3d':
      return X3D;
    case '.json':
      return JSON;
    case '.bin':
      return BIN;

    default:
      return '';
  }
};

/**
 * @param {Blob} blob
 * @returns {Promise<string | ArrayBuffer>} Converts a blob into a permanent URL
 */
export const blobToDataURL = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result); // "data:image/png;base64,..."
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * create a snapshot of an object that can be printed and serialized
 * @returns {object} an Object that is fully json serializable and printable
 */
export const snapshotObject = (obj, seen = new WeakMap()) => {
  // Primitives, null, or functions are returned as-is
  if (obj === null || typeof obj !== 'object') return obj;

  // Avoid circular references
  if (seen.has(obj)) return '[Circular]';
  seen.set(obj, true);

  const result = Array.isArray(obj) ? [] : {};

  const keys = Object.getOwnPropertyNames(obj);
  const symbols = Object.getOwnPropertySymbols(obj);

  for (const key of [...keys, ...symbols]) {
    let value;
    try {
      value = obj[key];
    } catch (e) {
      value = undefined;
    }

    // Recursively snapshot
    if (value && typeof value === 'object') {
      result[key] = snapshotObject(value, seen);
    } else {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Default GIM date format (and in software dev in general, less ambiguous than other formats)
 */
export const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * @param {string} dateString The date string.
 * @param {string} dateFormat the date format, defaults to yyyy-MM-dd.
 * @returns {Date} A Date Object representing the parsed date.
 */
export const parseGimDate = (dateString, dateFormat = DATE_FORMAT) =>
  parse(dateString, dateFormat, new Date());

/**
 * @param {Date} date The date object.
 * @param {string} dateFormat the date format, defaults to yyyy-MM-dd.
 * @returns {string} a date's string
 */
export const formatGimDate = (date, dateFormat = DATE_FORMAT) =>
  format(date, dateFormat);

const ensureUrlHasScheme = (urlStr) => {
  urlStr = urlStr.trim();

  if (!urlStr.startsWith('http://') && !urlStr.startsWith('https://')) {
    urlStr = 'https://' + urlStr;
  }

  return urlStr;
};

/**
 * @param {string} urlStr link to check if it's google maps or not
 * @returns {boolean} true if it's a google maps link
 */
export const isGoogleMapsLink = (urlStr) => {
  try {
    const url = new URL(ensureUrlHasScheme(encodeURI(urlStr.trim())));

    // valid map link domains
    const isGoogleDomain =
      url.hostname === 'google.com' ||
      url.hostname.endsWith('.google.com') ||
      url.hostname.includes('google.');

    // logDebug(
    //   'is this a gogl link?',
    //   isGoogleDomain,
    //   url.pathname,
    //   url.pathname.startsWith('/maps')
    // );
    return isGoogleDomain && url.pathname.startsWith('/maps');
  } catch (e) {
    logDebug('link crashed cuz', e, encodeURI(urlStr.trim()));
    return false;
  }
};

/**
 * @param {string} urlStr link to check if it's google maps or not
 * @returns {boolean} true if the input's a shortened maps link
 */
export const isShortGoogleMapsLink = (urlStr) => urlStr.includes('goo.gl');

/**
 * Used to extract lat/long from a google maps link
 * @param {string} googleMapLink google maps link
 * @returns {{lat: number, long: number}} lattitude and longitude from the url
 */
export const getCoordinatesFromGoogleMapLink = (googleMapLink) => {
  const reg = /maps\/[^@0-9]*@?(-?\d{2}\.\d+),([+-]?\d{1,3}\.\d+)/;

  const match = googleMapLink.match(reg);
  if (!match) {
    return { lat: 0, long: 0 };
  }

  const lat = parseFloat(match[1]);
  const long = parseFloat(match[2]);

  return { lat, long };
};

/**
 * Gets Project ID, object ID and subprojectID from an object's GUID
 * @param {string} guid guid of the file
 * @returns {[projectID: number, objectID: number, subProjectID?: number]} The Object's Identification
 */
export const getIDsFromGuid = (guid) => {
  guid = guid.split('.')[0];
  let projectID, objectID;
  let subProjectID = null;

  if (!guid.includes('_')) {
    var split = guid.split('-');
    projectID = Number(split[0]);
    objectID = Number(split[1]);
  } else {
    var splitA = guid.split('_');
    var splitB = splitA[1].split('-');
    projectID = Number(splitA[0]);
    subProjectID = Number(splitB[0]);
    objectID = Number(splitB[1]);
  }

  return [projectID, objectID, subProjectID];
};

/**
 * Gets an object's GUID from its projectID, objectID and subprojectID
 * @param {number} projectID The object's project ID
 * @param {number} objectID The object's ID
 * @param {number | null} subProjectID The object's subproject ID
 * @returns {string} The object's GUID
 */
export const GetGuidFromID = (projectID, objectID, subProjectID = null) => {
  return `${projectID}${subProjectID ? '_' + subProjectID : ''}-${objectID}`;
};

/**
 * Gets an object's GUID from its identification
 * @param {import('global').ObjectIdentification} objectIdentificaiton
 * @returns
 */
export const GetGuidFromIdentification = (objectIdentificaiton) => {
  return GetGuidFromID(
    objectIdentificaiton.projectId,
    objectIdentificaiton.objectId,
    objectIdentificaiton.subProjectId
  );
};

/**
 * @param {number} val the value to be clamped
 * @param {number} min the minimum value
 * @param {number} max the maximum value
 * @returns {number} The value clamped to the interval
 */
export const clampValue = (val, min, max) => {
  if (val < min) return min;
  if (val > max) return max;
  return val;
};

/**
 * Rounds a value to the specified precision
 * @param {number} val the value to be rounded
 * @param {number} precision the desired number of decimals to round
 * @returns {number} val rounded to precision decimals
 */
export const roundWithPrecision = (val, precision = 5) => {
  const tenPow = 10 ** precision;
  return Math.round(val * tenPow) / tenPow;
};

/**
 * Compares two app versions, returns true if the latest beats the current
 * @returns
 */
export const compareVersion = (current, latest) => {
  const [cMajor, cMinor, cPatch] = current.split('.').map(Number);
  const [lMajor, lMinor, lPatch] = latest.split('.').map(Number);

  if (lMajor > cMajor) return true;
  if (lMinor > cMinor) return true;
  if (lPatch > cPatch) return true;

  return false;
};
